import * as Sentry from '@sentry/react'
import { isAuthLockAbort } from './isAuthLockAbort'

let initialized = false

const texts = {
  de: {
    formTitle: 'Etwas aufgefallen?',
    messageLabel: 'Was möchtest du mitteilen?',
    messagePlaceholder: 'Ein Fehler, eine Idee, ein Wunsch – alles willkommen.',
    submitButtonLabel: 'Senden',
    cancelButtonLabel: 'Schließen',
    addScreenshotButtonLabel: 'Screenshot hinzufügen',
    removeScreenshotButtonLabel: 'Screenshot entfernen',
    successMessageText: 'Danke für dein Feedback!',
    isRequiredLabel: '(Pflichtfeld)',
    highlightToolText: 'Hervorheben',
    hideToolText: 'Verbergen',
    removeHighlightText: 'Entfernen',
  },
  en: {
    formTitle: 'Send Feedback',
    messageLabel: 'Description',
    messagePlaceholder: 'What happened? What did you expect?',
    submitButtonLabel: 'Submit',
    cancelButtonLabel: 'Cancel',
    addScreenshotButtonLabel: 'Add screenshot',
    removeScreenshotButtonLabel: 'Remove screenshot',
    successMessageText: 'Thanks for your feedback!',
    isRequiredLabel: '(required)',
  },
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  /*
    **Im E2E-Lauf startet Sentry hier gar nicht** (HAPPYTASK-17).

    Früher reichte `beforeSend: () => null` weiter unten — es hielt die
    Meldungen zurück, aber das SDK lief. Seit der Renderer über das
    Electron-SDK meldet, genügt das nicht mehr: Es schickt Ereignisse UND den
    Scope über das Protokoll `sentry-ipc://`, und registriert wird das vom
    **Main-Prozess** beim `init` — das im Testlauf bewusst ausbleibt
    (`shouldInitSentry`). Der Renderer redete also gegen eine Wand:

      Fetch API cannot load sentry-ipc://scope/sentry_key.
      URL scheme "sentry-ipc" is not supported.

    Ein Testlauf soll den Fehlerdienst überhaupt nicht anwerfen. Der
    Dev-Modus dagegen startet weiter (siehe unten) — dort hängt der
    Feedback-Knopf daran.
  */
  if (typeof window !== 'undefined' && window.api?.isE2E === true) return

  /*
    Aus dem Dev-Modus meldet die App keine Fehler.

    Ein HMR-Zwischenstand ist kein Bug: HAPPYTASK-16 war ein `ReferenceError`
    aus einem halb neu geladenen Modul — der Aufruf schon im Bundle, der Import
    noch nicht — und stand eine Stunde vor dem zugehörigen Commit im
    Issue-Stream. Null Nutzer, nichts zu tun. Jeder halbe Speicherstand einer
    Dev-Session kann so aussehen wie ein Produktfehler, kostet Triage-Zeit und
    verdeckt echte Meldungen. Im Dev sieht man den Fehler ohnehin in der Konsole.

    `init` läuft trotzdem, statt hier auszusteigen: `openFeedbackForm()` steigt
    bei `!initialized` aus, ein früher `return` würde also den Feedback-Knopf
    in der Titlebar ausgerechnet im Dev-Modus stumm schalten. `beforeSend`
    greift laut SDK-Typen NUR für Error-Events; Feedback läuft über
    `beforeSendFeedback` der Feedback-Integration und bleibt damit testbar.

    Kriterium ist `MODE`, nicht `import.meta.env.DEV`: Vitest setzt `DEV` auf
    `true`, aber `MODE` auf `'test'` — über `MODE` bleibt die Testumgebung im
    meldenden Zweig. Ausgeliefert wird nie mit `development`: `npm run package`
    und die CI-Release-Jobs setzen `NODE_ENV=production`, der Web-Build läuft
    über `vite build` (siehe `webpack.renderer.config.js`, `import.meta.env.MODE`).
  */
  const isDevelopment = import.meta.env.MODE === 'development'

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    /*
      Der einzige Ort, an dem Filtern verlässlich wirkt.

      Der `unhandledrejection`-Listener weiter unten ruft bei einem
      Auth-Lock-Abort `preventDefault()` — das hält das Dev-Overlay fern, aber
      keinen anderen Listener auf (dafür bräuchte es `stopImmediatePropagation`),
      und Sentrys eigener globaler Handler registriert sich hier in `init`, also
      VOR unserem. HAPPYTASK-14 kam denn auch mit
      `mechanism: auto.browser.global_handlers.onunhandledrejection` an: gemeldet
      hat es Sentry selbst, an unserem Swallow vorbei.

      `beforeSend` läuft für jedes Error-Event, gleich welcher Handler es
      aufgesammelt hat. Die ursprüngliche Ausnahme steht in
      `hint.originalException` — das Event selbst trägt nur noch die serialisierte
      Fassung.
    */
    beforeSend: (event, hint) => {
      if (isDevelopment) return null
      // Ein E2E-Lauf der gepackten App ist kein Nutzer (HAPPYTASK-17).
      if (typeof window !== 'undefined' && window.api?.isE2E === true) return null
      if (isAuthLockAbort(hint?.originalException)) return null
      return event
    },

    integrations: [
      Sentry.feedbackIntegration({
        autoInject: false,
      }),
    ],
  })
  initialized = true

  // Catch JS errors and unhandled promise rejections that escape React's error
  // boundary (e.g. async work in event handlers, fire-and-forget promises).
  // Sentry's React SDK does NOT auto-install these globals when no
  // BrowserApiErrors integration is configured — we add them explicitly.
  window.addEventListener('error', (e) => {
    Sentry.captureException(e.error ?? new Error(e.message), {
      tags: { source: 'window.error' },
    })
  })

  window.addEventListener('unhandledrejection', (e) => {
    if (isAuthLockAbort(e.reason)) {
      // Verschluckt: hält im Dev-Modus das Fehler-Overlay vom Fenster fern und
      // hinterlässt eine Spur statt einer nichtssagenden Exception.
      e.preventDefault()
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message: 'Supabase auth lock acquire timed out',
      })
      return
    }
    Sentry.captureException(
      e.reason instanceof Error ? e.reason : new Error(String(e.reason)),
      { tags: { source: 'unhandledrejection' } },
    )
  })
}

// Block keyboard shortcuts while typing in the Sentry feedback form.
// Events from the Shadow DOM bubble to the main document — intercept them
// in the capture phase before any app handler sees them.
function installKeyboardGuard() {
  window.addEventListener('keydown', (e) => {
    const path = e.composedPath()
    if (path.some(el => el instanceof HTMLElement && el.id === 'sentry-feedback')) {
      e.stopPropagation()
    }
  }, true) // capture phase
}
installKeyboardGuard()

function getCurrentLanguage(): 'de' | 'en' {
  const lang = localStorage.getItem('language') ?? navigator.language
  return lang.startsWith('de') ? 'de' : 'en'
}

/** Opens the Sentry feedback form in the current app language. No-op if Sentry is not initialized. */
export function openFeedbackForm() {
  if (!initialized) return

  const lang = getCurrentLanguage()
  const localizedFeedback = Sentry.feedbackIntegration({
    colorScheme: 'system',
    enableScreenshot: true,
    showName: false,
    showEmail: true,
    autoInject: false,
    showBranding: false,
    useSentryUser: { email: 'email' },
    ...texts[lang],
  })

  localizedFeedback.createForm().then(form => {
    form.appendToDom()
    form.open()
  }).catch(err => {
    console.error('[Sentry] Failed to open feedback form:', err)
  })
}
